import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { Adapt } from './lib/adapt.js';

// Contrat du rig partagé : interdit. Le réseau est sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'mycelium',
  family: 'organic',
  danceability: 0.32,
  requiresTier: 0,
  energyLevel: 0.20,
  bpmMin: 50,
  bpmMax: 130,
  movement: 0.25,
  tonality: 0.75,
  enabled: true
};

// MYCELIUM : un réseau vivant qui se tisse, en fils d'or sur fond noir — la
// simulation de Physarum (Jeff Jones, 2010). Ambient rythmée, IDM douce,
// néo-classique, downtempo. Règles R1 à R19, preset lounge (R9), module
// adapt.js, démarrage à chaud (« R20 » des briefs).
//
// LA SIMULATION, entièrement sur le GPU :
// - les AGENTS (100 000 au tier 2, 50 000 au tier 1, 25 000 au tier 0) : une
//   texture flottante ping-pong, un texel par agent = (x, y) normalisés et
//   le cap. Float32 quand le GPU sait y rendre (le Pi oui), sinon demi-
//   flottant : en demi-flottant, une position près de 1 ne se résout qu'au
//   demi-pixel de la carte, et des pas d'un pixel arrondis biaisent les caps.
// - la CARTE DE TRACES : demi-résolution de l'écran, demi-flottante, ping-pong.
// Un pas de simulation :
//   1. AGENTS : chaque agent lit la trace en trois capteurs devant lui
//      (gauche, centre, droite, à la distance SO, à ±SA), tourne vers le plus
//      fort (règle de Jones : tout droit si le centre gagne, au hasard si les
//      deux côtés gagnent, sinon vers le côté fort ; ± RA, plus un peu de
//      bruit), avance de SS pixels (le monde est un tore : pas de bord) ;
//   2. DÉPÔT : les agents sont dessinés en POINTS d'un pixel, en addition,
//      dans la carte (le sommet lit sa position dans la texture des agents) ;
//   3. DIFFUSION + ÉVAPORATION : flou 3 x 3 mélangé à la valeur brute, puis
//      décroissance multiplicative et une petite soustraction, pour que la
//      carte laissée seule revienne au noir pur (R15) ; les NUTRIMENTS y
//      injectent leur trace.
// Le pas est à cadence FIXE (30 pas par seconde x adapt.motion, au plus 3 par
// image) : l'évaporation par pas ne dépend donc pas du nombre d'images.
//
// LES PRÉRÉGLAGES (SA, SO, RA, SS, évaporation, dépôt) changent la morphologie :
// veines épaisses, réseau fin et régulier, mailles, tourbillons, réseau
// nerveux. La scène glisse de l'un à l'autre (cycle de 75 s, interpolation
// douce) et adapt.intensity tire les paramètres vers le préréglage calme
// (lent, veines épaisses) ou vers le nerveux (fin, rapide).
//
// DÉMARRAGE À CHAUD : à l'activation, 400 pas tournent avant la première
// image visible (100 par image, l'affichage reste noir pendant ces quatre
// images de fondu d'entrée) : le réseau est déjà formé, jamais de bruit.
//
// LES NUTRIMENTS (preset lounge, R9 : rien sur le temps) : toutes les 8 s
// environ, une source de trace apparaît au centre de composition (R19) ou en
// un point de la composition ; elle monte en 1 s, nourrit 5 s, s'épuise en 2 s ;
// le réseau converge vers elle puis l'abandonne.
//
// LE RENDU : la trace -> 1 - exp(-k t), lue dans une palette noir, brun doré,
// or, blanc chaud (seuls les chemins les plus denses passent le seuil de
// bloom) ; une palette froide (bleus, cyans) sur un morceau sur trois (graine
// du morceau). Agrandi en LinearFilter (R2), contraste en courbe pour des fils
// nets.
//
// Debug : ?mycopreset=<0..4> (préréglage fixe), ?mycoagents=<n>, ?mycofood=1
// (un nutriment toutes les 3 s), ?mycocold=0|1 (palette), ?mycoprobe=1 (window.__mycelium).

const TIER = {
  0: { agents: 25000 },
  1: { agents: 50000 },
  2: { agents: 100000 }
};
const TRAIL_SCALE = 0.5;
const STEPS_PER_S = 30, MAX_STEPS_PER_FRAME = 3;
const WARM_STEPS = 400, WARM_PER_FRAME = 100;
const PRESET_CYCLE_S = 75;
const FOOD_PERIOD_S = 8, FOOD_RISE_S = 1, FOOD_HOLD_S = 5, FOOD_FALL_S = 2;
const SAFE_FLOOR = 0.45;
const GAIN = 0.20;                      // mesuré : fond ~3, chemins faibles ~30, forts 100-300 -> 0, ~0,35, ~0,9
const DEG = Math.PI / 180;

// [SA (deg), SO (px), RA (deg), SS (px/pas), évaporation (part gardée par pas), dépôt]
export const PRESETS = [
  { name: 'veines', sa: 22.5, so: 14, ra: 22.5, ss: 0.75, keep: 0.94, dep: 3.0 },
  { name: 'réseau fin', sa: 45, so: 9, ra: 45, ss: 1.0, keep: 0.88, dep: 2.2 },
  { name: 'mailles', sa: 60, so: 22, ra: 30, ss: 1.0, keep: 0.91, dep: 2.5 },
  { name: 'tourbillons', sa: 30, so: 5, ra: 70, ss: 1.3, keep: 0.90, dep: 2.4 },
  { name: 'nerveux', sa: 35, so: 7, ra: 40, ss: 1.8, keep: 0.84, dep: 2.0 }
];
const CALM = PRESETS[0], NERVOUS = PRESETS[4];
const KEYS = ['sa', 'so', 'ra', 'ss', 'keep', 'dep'];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function smooth(x) { const t = clamp01(x); return t * t * (3 - 2 * t); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}
function hashSeed(str) { let h = 2166136261 >>> 0; const t = String(str || ''); for (let i = 0; i < t.length; i++) { h ^= t.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; } return h || 1; }
function rng(seed) { let s = (seed >>> 0) || 1; return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }

// les paramètres à un instant : le préréglage de la scène (interpolé), tiré
// vers le calme ou le nerveux par l'intensité
export function paramsAt(sceneT, intensity, forced) {
  let base;
  if (forced != null) base = PRESETS[forced];
  else {
    const x = sceneT / PRESET_CYCLE_S, i = Math.floor(x), f = smooth((x - i - 0.6) / 0.4);   // 60 % du cycle sur un préréglage, 40 % de fondu
    const a = PRESETS[((i % 5) + 5) % 5], b = PRESETS[(((i + 1) % 5) + 5) % 5];
    base = {}; for (const k of KEYS) base[k] = lerp(a[k], b[k], f);
  }
  const I = clamp01(intensity);
  const target = I < 0.5 ? CALM : NERVOUS, w = Math.min(1, Math.abs(I - 0.5) * 1.2);
  const out = {}; for (const k of KEYS) out[k] = lerp(base[k], target[k], forced != null ? 0 : w);
  return out;
}
// l'enveloppe d'un nutriment (0..1) à l'âge a (s)
export function foodEnvelope(a) {
  if (a < 0) return 0;
  if (a < FOOD_RISE_S) return smooth(a / FOOD_RISE_S);
  if (a < FOOD_RISE_S + FOOD_HOLD_S) return 1;
  return 1 - smooth((a - FOOD_RISE_S - FOOD_HOLD_S) / FOOD_FALL_S);
}
export function agentTextureSize(n) { const s = Math.ceil(Math.sqrt(n)); return s; }

// ------------------------------------------------------------------ GLSL
const QUAD_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
// 1. les agents
const AGENT_FRAGMENT = `
  precision highp float;
  uniform sampler2D tAgents;
  uniform sampler2D tTrail;
  uniform vec2 uTrailSize;
  uniform float uSA, uSO, uRA, uSS;
  uniform float uStep;
  varying vec2 vUv;
  float hash(vec2 p) { vec3 q = fract(vec3(p.xyx) * 0.1031); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
  float sense(vec2 pos, float a) {
    vec2 q = pos + vec2(cos(a), sin(a)) * uSO / uTrailSize;
    return texture2D(tTrail, fract(q)).r;
  }
  void main() {
    vec4 s = texture2D(tAgents, vUv);
    vec2 pos = s.xy; float ang = s.z;
    float F = sense(pos, ang), L = sense(pos, ang + uSA), R = sense(pos, ang - uSA);
    // (graine bornée : la TV calcule en fp16, R « TV mediump »)
    float rnd = hash(vUv * 1024.0 + vec2(mod(uStep, 977.0), mod(uStep * 0.37, 619.0)));
    if (F > L && F > R) { }
    else if (F < L && F < R) ang += (rnd < 0.5 ? -1.0 : 1.0) * uRA;
    else if (L > R) ang += uRA;
    else if (R > L) ang -= uRA;
    ang += (rnd - 0.5) * 0.18;
    pos = fract(pos + vec2(cos(ang), sin(ang)) * uSS / uTrailSize);
    gl_FragColor = vec4(pos, mod(ang, 6.2831853), 1.0);
  }
`;
// 2. le dépôt : un point d'un pixel par agent, en addition
const DEPOSIT_VERTEX = `
  precision highp float;
  attribute vec2 aRef;
  uniform sampler2D tAgents;
  void main() {
    vec2 p = texture2D(tAgents, aRef).xy;
    gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
    gl_PointSize = 1.0;
  }
`;
const DEPOSIT_FRAGMENT = `
  precision highp float;
  uniform float uDep;
  void main() { gl_FragColor = vec4(uDep, 0.0, 0.0, 1.0); }
`;
// 3. diffusion + évaporation + nutriments
const DIFFUSE_FRAGMENT = `
  precision highp float;
  uniform sampler2D tTrail;
  uniform vec2 uTrailSize;
  uniform float uKeep;
  uniform vec4 uFood[2];          // x, y (normalisés), force, rayon (px)
  varying vec2 vUv;
  void main() {
    vec2 px = 1.0 / uTrailSize;
    float c = texture2D(tTrail, vUv).r;
    float sum = 0.0;
    for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) sum += texture2D(tTrail, fract(vUv + vec2(float(i), float(j)) * px)).r;
    // le flou 3 x 3 à 30 % : les fils restent fins (à 50 % ils faisaient 19 px à 1080p)
    float v = mix(c, sum / 9.0, 0.3);
    v = max(0.0, v * uKeep - 0.002);
    for (int k = 0; k < 2; k++) {
      vec4 f = uFood[k];
      if (f.z > 0.0) { vec2 d = (vUv - f.xy) * uTrailSize; v += f.z * exp(-dot(d, d) / (f.w * f.w)); }
    }
    gl_FragColor = vec4(min(v, 1000.0), 0.0, 0.0, 1.0);   // (à 200 les chemins forts plafonnaient)
  }
`;
// 4. l'affichage
const DISPLAY_FRAGMENT = `
  precision highp float;
  uniform sampler2D tTrail;
  uniform float uGain;
  uniform float uVisible;
  uniform float uCold;
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  float safeAtt(vec2 uv) {
    vec2 ndc = uv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  vec3 gold(float v) {
    vec3 c = mix(vec3(0.0), vec3(0.20, 0.09, 0.02), smoothstep(0.02, 0.22, v));
    c = mix(c, vec3(0.62, 0.38, 0.08), smoothstep(0.22, 0.52, v));
    c = mix(c, vec3(0.95, 0.70, 0.26), smoothstep(0.52, 0.82, v));
    c = mix(c, vec3(1.08, 0.94, 0.72), smoothstep(0.93, 1.0, v));    // seuls les plus denses passent le seuil de bloom (un bloom large voilait tout le fond)
    return c;
  }
  vec3 cold(float v) {
    vec3 c = mix(vec3(0.0), vec3(0.02, 0.07, 0.18), smoothstep(0.02, 0.22, v));
    c = mix(c, vec3(0.06, 0.32, 0.62), smoothstep(0.22, 0.52, v));
    c = mix(c, vec3(0.25, 0.78, 0.92), smoothstep(0.52, 0.82, v));
    c = mix(c, vec3(0.85, 1.02, 1.10), smoothstep(0.93, 1.0, v));
    return c;
  }
  void main() {
    float t = texture2D(tTrail, vUv).r;
    // la trace -> 0..1, puis une courbe qui creuse les bords des fils (nets)
    float v = 1.0 - exp(-t * uGain);
    // (le fond, où errent les agents isolés, reste noir pur)
    v = smoothstep(0.30, 1.0, v);
    vec3 col = mix(gold(v), cold(v), uCold);
    gl_FragColor = vec4(col * safeAtt(vUv) * uVisible, 1.0);
  }
`;

// =============================================================================

export class MyceliumVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    const na = parseInt(urlParam('mycoagents'), 10);
    this._agents = Number.isFinite(na) && na >= 100 ? Math.min(400000, na) : (TIER[this._tier] || TIER[1]).agents;
    const fp = parseInt(urlParam('mycopreset'), 10);
    this._forcePreset = Number.isFinite(fp) && fp >= 0 && fp < PRESETS.length ? fp : null;
    this._foodDebug = urlParam('mycofood') === '1';
    const fc = urlParam('mycocold');
    this._forceCold = fc === '1' ? 1 : fc === '0' ? 0 : null;
    // lounge : bloom faible et haut (0,08 au-dessus de 0,90) : à 0,30 / 0,82 puis 0,15 il voilait le fond de brun ; aucun flash
    this._post = { bloomStrength: 0.08, bloomThreshold: 0.90, grain: 0.01, vignette: 0.35, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
    if (urlParam('mycoprobe') === '1' && typeof window !== 'undefined') window.__mycelium = this;
  }

  get postProcessing() { return this._post; }

  init(scene) {
    this._scene = scene;
    this._size = new THREE.Vector2();
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;
    const S = this._S = agentTextureSize(this._agents);
    // les agents : Float32 si le GPU sait y rendre, sinon demi-flottant
    this._agentType = THREE.FloatType;
    const aopt = { type: this._agentType, format: THREE.RGBAFormat, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, depthBuffer: false, stencilBuffer: false, generateMipmaps: false };
    this._agentsRT = [new THREE.WebGLRenderTarget(S, S, aopt), new THREE.WebGLRenderTarget(S, S, aopt)];
    this._a = 0;
    const topt = { type: THREE.HalfFloatType, format: THREE.RGBAFormat, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, wrapS: THREE.RepeatWrapping, wrapT: THREE.RepeatWrapping, depthBuffer: false, stencilBuffer: false, generateMipmaps: false };
    this._trailRT = [new THREE.WebGLRenderTarget(16, 16, topt), new THREE.WebGLRenderTarget(16, 16, topt)];
    this._t = 0;
    this._trailSize = new THREE.Vector2(16, 16);

    const quad = (mat) => { const s = new THREE.Scene(); const m = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat); m.frustumCulled = false; s.add(m); return { scene: s, mesh: m }; };
    this._agentU = { tAgents: { value: null }, tTrail: { value: null }, uTrailSize: { value: this._trailSize }, uSA: { value: 0 }, uSO: { value: 0 }, uRA: { value: 0 }, uSS: { value: 0 }, uStep: { value: 0 } };
    this._agentPass = quad(new THREE.ShaderMaterial({ uniforms: this._agentU, vertexShader: QUAD_VERTEX, fragmentShader: AGENT_FRAGMENT, depthTest: false, depthWrite: false }));
    this._diffU = { tTrail: { value: null }, uTrailSize: { value: this._trailSize }, uKeep: { value: 0.9 }, uFood: { value: [new THREE.Vector4(), new THREE.Vector4()] } };
    this._diffPass = quad(new THREE.ShaderMaterial({ uniforms: this._diffU, vertexShader: QUAD_VERTEX, fragmentShader: DIFFUSE_FRAGMENT, depthTest: false, depthWrite: false }));
    // le dépôt : N points, chacun l'adresse de son agent
    const refs = new Float32Array(S * S * 2), dummy = new Float32Array(S * S * 3);
    for (let j = 0; j < S; j++) for (let i = 0; i < S; i++) { const k = j * S + i; refs[2 * k] = (i + 0.5) / S; refs[2 * k + 1] = (j + 0.5) / S; }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(dummy, 3));
    g.setAttribute('aRef', new THREE.BufferAttribute(refs, 2));
    g.setDrawRange(0, this._agents);
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e3);
    this._depU = { tAgents: { value: null }, uDep: { value: 2 } };
    this._depPoints = new THREE.Points(g, new THREE.ShaderMaterial({ uniforms: this._depU, vertexShader: DEPOSIT_VERTEX, fragmentShader: DEPOSIT_FRAGMENT, depthTest: false, depthWrite: false, transparent: true, blending: THREE.AdditiveBlending }));
    this._depPoints.frustumCulled = false;
    this._depScene = new THREE.Scene(); this._depScene.add(this._depPoints);
    this._dispU = { tTrail: { value: null }, uGain: { value: 0.10 }, uVisible: { value: 0 }, uCold: { value: 0 },
      uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._dispU, vertexShader: QUAD_VERTEX, fragmentShader: DISPLAY_FRAGMENT, depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    this._display.onBeforeRender = (renderer) => this._render(renderer);
    scene.add(this._display);

    this._adapt = new Adapt(); this._ad = this._adapt.get();
    this._sceneT = 0; this._simClock = 0; this._stepN = 0; this._pending = 0;
    this._warmDone = 0; this._needSeed = true; this._paused = false;
    this._food = []; this._nextFood = 2.5; this._foodN = 0; this._seedSeen = null; this._cold = 0;
    this._params = paramsAt(0, 0.4, this._forcePreset);
    this._stats = { steps: 0, foods: 0, warm: 0 };
  }

  // les agents au départ : uniformes sur l'écran, caps au hasard (invisibles : le démarrage à chaud les organise)
  _seedAgents(renderer) {
    const S = this._S, data = new Float32Array(S * S * 4), r = rng(1234567);
    for (let k = 0; k < S * S; k++) { data[4 * k] = r(); data[4 * k + 1] = r(); data[4 * k + 2] = r() * Math.PI * 2; data[4 * k + 3] = 1; }
    const tex = new THREE.DataTexture(data, S, S, THREE.RGBAFormat, THREE.FloatType);
    tex.needsUpdate = true;
    const copyU = { tAgents: { value: tex } };
    const copy = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: copyU, vertexShader: QUAD_VERTEX, fragmentShader: 'precision highp float; uniform sampler2D tAgents; varying vec2 vUv; void main() { gl_FragColor = texture2D(tAgents, vUv); }', depthTest: false, depthWrite: false }));
    copy.frustumCulled = false;
    const s = new THREE.Scene(); s.add(copy);
    for (const rt of this._agentsRT) { renderer.setRenderTarget(rt); renderer.render(s, this._ortho); }
    for (const rt of this._trailRT) { renderer.setRenderTarget(rt); renderer.setClearColor(0x000000, 1); renderer.clear(true, false, false); }
    tex.dispose(); copy.geometry.dispose(); copy.material.dispose();
    this._needSeed = false; this._warmDone = 0;
  }

  // un pas de simulation (agents -> dépôt -> diffusion)
  _step(renderer) {
    const p = this._params;
    const A = this._agentU;
    A.tAgents.value = this._agentsRT[this._a].texture; A.tTrail.value = this._trailRT[this._t].texture;
    A.uSA.value = p.sa * DEG; A.uSO.value = p.so; A.uRA.value = p.ra * DEG; A.uSS.value = p.ss; A.uStep.value = this._stepN;
    renderer.setRenderTarget(this._agentsRT[this._a ^ 1]);
    renderer.render(this._agentPass.scene, this._ortho);
    this._a ^= 1;
    // le dépôt dans la carte courante (addition, sans effacer)
    this._depU.tAgents.value = this._agentsRT[this._a].texture; this._depU.uDep.value = p.dep;
    renderer.setRenderTarget(this._trailRT[this._t]);
    const ac = renderer.autoClear; renderer.autoClear = false;
    renderer.render(this._depScene, this._ortho);
    renderer.autoClear = ac;
    // diffusion + évaporation vers l'autre carte
    this._diffU.tTrail.value = this._trailRT[this._t].texture; this._diffU.uKeep.value = p.keep;
    renderer.setRenderTarget(this._trailRT[this._t ^ 1]);
    renderer.render(this._diffPass.scene, this._ortho);
    this._t ^= 1;
    this._stepN++; this._stats.steps++;
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState != null && profile.playbackState !== 'PLAYING';
    const dt = this._paused ? 0 : Math.min(Math.max(deltaTime, 0), 0.1);
    const a = this._ad = this._adapt.update(dt, profile);
    if (this._paused) return;                     // pause : gel (aucun pas, la carte reste)
    this._sceneT += dt;
    // la graine du morceau : palette froide sur un morceau sur trois
    const seed = hashSeed(this._adapt.simulated ? 'sim:' + this._adapt.simulated : (profile.trackId || ((profile.title || '') + '|' + (profile.artist || ''))));
    if (seed !== this._seedSeen) { this._seedSeen = seed; this._coldTarget = this._forceCold != null ? this._forceCold : (seed % 3 === 0 ? 1 : 0); if (this._sceneT < 0.5) this._cold = this._coldTarget; }
    this._cold += ((this._coldTarget || 0) - this._cold) * (1 - Math.exp(-dt / 3));
    this._params = paramsAt(this._sceneT, a.intensity, this._forcePreset);
    // la cadence fixe des pas
    this._pending = Math.min(MAX_STEPS_PER_FRAME, this._pending + dt * STEPS_PER_S * (0.6 + 0.6 * a.motion));
    // LES NUTRIMENTS
    if (this._warmDone >= WARM_STEPS && this._sceneT >= this._nextFood) {
      const r = rng(this._seedSeen + this._foodN * 7919);
      const spots = [[0.5, 0.5], [0.5 - 0.22, 0.5 + 0.18], [0.5 + 0.24, 0.5 - 0.16], [0.5 + 0.2, 0.5 + 0.22], [0.5 - 0.25, 0.5 - 0.2]];
      const sp = this._foodN % 3 === 0 ? spots[0] : spots[1 + Math.floor(r() * 4)];
      this._food.push({ x: sp[0], y: sp[1], born: this._sceneT });
      this._food = this._food.slice(-2);
      this._foodN++; this._stats.foods++;
      this._nextFood = this._sceneT + (this._foodDebug ? 3 : FOOD_PERIOD_S * (0.8 + 0.4 * r()));
    }
    const F = this._diffU.uFood.value;
    for (let k = 0; k < 2; k++) {
      const f = this._food[k];
      const e = f ? foodEnvelope(this._sceneT - f.born) : 0;
      if (f && e > 0) F[k].set(f.x, f.y, 1.2 * e, 7); else F[k].set(0, 0, 0, 1);
    }
    this._dispU.uCold.value = this._cold;
  }

  _render(renderer) {
    const prev = renderer.getRenderTarget();
    if (prev) this._size.set(prev.width, prev.height); else renderer.getDrawingBufferSize(this._size);
    const w = Math.max(16, Math.round(this._size.x * TRAIL_SCALE)), h = Math.max(16, Math.round(this._size.y * TRAIL_SCALE));
    if (this._trailRT[0].width !== w || this._trailRT[0].height !== h) {
      for (const rt of this._trailRT) rt.setSize(w, h);
      this._trailSize.set(w, h);
      this._needSeed = true;                       // nouvelle carte : nouveau démarrage à chaud
    }
    const cc = new THREE.Color(); renderer.getClearColor(cc); const ca = renderer.getClearAlpha();
    if (this._needSeed) this._seedAgents(renderer);
    // DÉMARRAGE À CHAUD : 100 pas par image tant que le réseau n'est pas formé, affichage noir
    if (this._warmDone < WARM_STEPS) {
      const n = Math.min(WARM_PER_FRAME, WARM_STEPS - this._warmDone);
      for (let i = 0; i < n; i++) this._step(renderer);
      this._warmDone += n; this._stats.warm = this._warmDone;
    } else if (!this._paused) {
      while (this._pending >= 1) { this._step(renderer); this._pending -= 1; }
    }
    renderer.setClearColor(cc, ca);
    renderer.setRenderTarget(prev);
    this._dispU.tTrail.value = this._trailRT[this._t].texture;
    this._dispU.uVisible.value = this._warmDone >= WARM_STEPS ? 1 : 0;
    // le gain d'affichage suit le dépôt (une trace d'équilibre ~ dépôt / (1 - évaporation))
    const p = this._params;
    // (ramené à la densité d'agents : le même réseau au tier 0 et au tier 2)
    const density = this._agents / (this._trailSize.x * this._trailSize.y);
    this._dispU.uGain.value = GAIN * (1 - p.keep) / (Math.max(0.5, p.dep) * Math.max(0.02, density));
  }

  getProbe() {
    const p = this._params;
    return { tier: this._tier, agents: this._agents, texture: this._S, trail: [this._trailSize.x, this._trailSize.y], warm: this._warmDone, steps: this._stepN, sceneT: +this._sceneT.toFixed(2),
      params: Object.fromEntries(KEYS.map((k) => [k, +p[k].toFixed(3)])), foods: this._stats.foods, activeFood: this._food.filter((f) => foodEnvelope(this._sceneT - f.born) > 0).length,
      intensity: +this._ad.intensity.toFixed(3), cold: +this._cold.toFixed(2), sim: this._adapt.simulated };
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); this._display.geometry.dispose(); this._display.material.dispose(); }
    for (const q of [this._agentPass, this._diffPass]) if (q) { q.mesh.geometry.dispose(); q.mesh.material.dispose(); }
    if (this._depPoints) { this._depPoints.geometry.dispose(); this._depPoints.material.dispose(); }
    for (const rt of (this._agentsRT || []).concat(this._trailRT || [])) rt.dispose();
    if (this._adapt) this._adapt.dispose();
    if (typeof window !== 'undefined' && window.__mycelium === this) window.__mycelium = null;
    this._scene = null;
  }
}
